import '@divkitframework/divkit/dist/client.css?inline';

import { createDivkitModelCreatorByDefault } from '@mail/3promo/divkit';
import { getPromoManagerSync, initDefaultPromoManager } from '@mail/3promo/utils';
import { coreLoggerDispatchError, coreLoggerDispatchKnownError } from '@mail-core/logger';
import { tryCatch } from '@mail-core/std/fn/sugar/tryCatch';
import { parseJSONSafe } from '@mail-core/std/fn/transform/parseJSONSafe';
import { truthy } from '@mail-core/std/fn/validate/truthy';
import { getFeature } from 'Cloud/Application/FeaturesEs6';
import config from 'Cloud/config';
import { xray } from 'lib/xray';
import { features } from 'reactApp/mail-features';
import { removePromo } from 'reactApp/modules/promo/promo.module';
import { isPromoCanBeShown } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { advAucFullscreenCreator } from 'reactApp/modules/promoNew/items/advAucFullscreenCreator';
import { aiGenerateAndEditFakedoorCreator } from 'reactApp/modules/promoNew/items/aiGenerateAndEditFakedoorCreator';
import { aiLetterStyleCreator } from 'reactApp/modules/promoNew/items/aiLetterStyleCreator';
import { aiTariffPromoCreator } from 'reactApp/modules/promoNew/items/aiTariffPromoCreator';
import { composeFakeDoorCreator } from 'reactApp/modules/promoNew/items/fakedoorCreator';
import { neurofilterPromoCreator } from 'reactApp/modules/promoNew/items/neurofilterPromoCreator';
import { pdfAutosaveCreator } from 'reactApp/modules/promoNew/items/pdfAutosaveCreator';
import { pdfWorkWithPagesCreator } from 'reactApp/modules/promoNew/items/pdfWorkWithPagesCreator';
import { unlimitedDocumentsAsidePromoCreator } from 'reactApp/modules/promoNew/items/unlimitedDocumentsAsidePromoCreator';
import { unlimitedDocumentsPromoCreator } from 'reactApp/modules/promoNew/items/unlimitedDocumentsPromoCreator';
import { defaultReactRenderDeps } from 'reactApp/modules/promoNew/render/react';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { safetyPersonalLocalStorage } from 'reactApp/storage';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';
import { windowOpen } from 'reactApp/utils/opener';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import style from './index.css';

export const initPromoManager = () => {
    let manager = getPromoManagerSync();

    if (manager) {
        return manager;
    }

    manager = initDefaultPromoManager(
        {
            closeLimit: 3,
            showLimit: 10,
            showInterval: 30,
            showDelay: 600,
            promoTimeout: features.getAsDuration('promo-timeout', '10m'),
            promoDisabled: features.has('promo-disabled'),
            showId: features.getAsString('promo-force-show-id'),
            debugLog: features.has('3promo-debug'),
            ignoreShowInterval: features.has('ignore-show-interval'),
            ignoreGlobalShowInterval: features.has('ignore-global-show-interval'),
            ignoreShowLimit: features.has('ignore-show-limit'),
            override: {
                globalSettings: {
                    test: (resolve, reject) => {
                        const state = reduxStore.getState();
                        const resolveOk = isPromoCanBeShown(state);

                        const storage = getCurrentStorage(state);
                        const { action = '' } = SettingsSelectors.getQueryParams(state);
                        if (storage === EStorageType.quotaCleaner && !action) {
                            sendXray(['promo-gl-test', resolveOk ? 'resolve' : 'reject']);
                        }

                        if (resolveOk) {
                            return resolve();
                        }

                        return reject();
                    },
                },
                models: {
                    base: {
                        callbacks: {
                            onHide: () => reduxStore.dispatch(removePromo(EPromoType.threePromo)),
                        },
                    },
                    notify: {
                        getNotifyContainer: () => document.querySelector('#notify-parent')!,
                    },
                },
            },
        },
        {
            features,
            stores: {
                /**
                 * Передается именно так, а не `localStorage: safetyPersonalLocalStorage`, т.к. эти методы не являются прямыми ключами,
                 * а оно не пробует достать, а перебирает доступные ключи объект. В теории - баг di-registry
                 */
                localStorage: {
                    getAsJSON: (key) => safetyPersonalLocalStorage.getAsJSON(key),
                    removeItem: (key) => safetyPersonalLocalStorage.removeItem(key),
                    setAsJSON: (key, value) => safetyPersonalLocalStorage.setAsJSON(key, value),
                },
            },
            xray,
            defaultReactRenderDeps,
        },
        [
            // Всегда оставлять в топе
            advAucFullscreenCreator,
            // -----------------------
            aiTariffPromoCreator,
            neurofilterPromoCreator,
            unlimitedDocumentsPromoCreator,
            unlimitedDocumentsAsidePromoCreator,
            aiLetterStyleCreator,
            aiGenerateAndEditFakedoorCreator,
            pdfAutosaveCreator,
            pdfWorkWithPagesCreator,
            composeFakeDoorCreator(),
            ...tryCatch(
                () =>
                    createDivkitModelCreatorByDefault({
                        elementClass: style.divkit,
                        elementCss: {
                            hide: { classList: [style.divkitHide] },
                            show: {},
                        },
                        xraySend: (...args) => xray.send(...args),
                        windowOpen,
                        coreLoggerDispatchKnownError,
                        features3promo: {
                            getItem(key) {
                                const data = getFeature(key);
                                return typeof data === 'string' ? parseJSONSafe(data, data) : data;
                            },
                            keys() {
                                return config.get('FEATURES')?.enabled || [];
                            },
                        },
                        getTheme: () => (isDarkThemeModeEnabled() ? 'dark' : 'light'),
                        subscribeTheme: () => () => {}, // ?
                        addCustomParams: {
                            project: 'cloud',
                        },
                    }),
                (error) => {
                    coreLoggerDispatchError(error, { location: 'divkit-model' });
                    return [];
                }
            ),
        ].filter(truthy)
    );

    // паузим менеджер
    manager.pause();

    return manager;
};
